import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import type { CampingSpot, Coordinates } from '../types';
import { getDistance } from '../services/geometryUtils';
import { GeofenceCheckIn } from '../src/plugins/geofence';
import {
  selectNearestEligibleSpots,
  buildGeofenceRegions,
  shouldResyncGeofences,
} from '../services/geofenceManager';
import { PROXIMITY_DWELL_MS } from '../services/spotProximity';
import { isDismissedRecently } from '../components/spots/AutoCheckInBanner';
import { GEOFENCE_MAX_REGIONS, GEOFENCE_RADIUS_M, GEOFENCE_OPTOUT_KEY } from '../constants';

/** Har brukeren skrudd av bakgrunns-innsjekk via profil-toggelen? */
const isOptedOut = (): boolean => {
  try { return localStorage.getItem(GEOFENCE_OPTOUT_KEY) === '1'; } catch { return false; }
};

// Kun aktiv på native OG når selve native-pluginen finnes i denne bygg-en. Uten
// pluginsjekken ville en OTA av denne JS-en til en eldre native-app (uten
// geofence-pluginen) kalt et uimplementert plugin → runtime-feil. OTA pakker HELE
// arbeidstreet, så dette må degradere stille til pluginen er butikk-releaset.
const isNative = Capacitor.isNativePlatform();
const pluginAvailable = isNative && Capacitor.isPluginAvailable('GeofenceCheckIn');

// Fast varseltekst (matcher forgrunns-pushen i checkInNotifications.ts). i18n kan
// komme senere — begge tekst-kildene bør da oppdateres sammen.
const buildText = (spot: CampingSpot) => ({
  title: 'Vil du sjekke inn?',
  body: `Du har vært ved ${spot.name || 'en plass'} en stund. Sjekk inn nå!`,
});

/**
 * Registrerer native OS-geofences rundt de nærmeste innsjekk-egnede plassene og
 * kaller `onCheckInTriggered` når en dwell-trigger slår til mens appen lever.
 * Når appen er lukket poster native varselet selv (tap → deeplink, håndteres i
 * App-oppstart). No-op på web og når `enabled` er av.
 *
 * Ber IKKE om «Tillat hele tiden» automatisk — det er en sensitiv systemprompt
 * som bør trigges av en eksplisitt bruker-handling. Synker kun når tillatelsen
 * allerede er gitt.
 */
export function useGeofenceCheckIn({
  enabled,
  spots,
  position,
  currentUserUid,
  onCheckInTriggered,
}: {
  enabled: boolean;
  spots: CampingSpot[];
  position: Coordinates | null;
  currentUserUid?: string | null;
  onCheckInTriggered: (spotId: string) => void;
}): void {
  const anchorRef = useRef<Coordinates | null>(null);
  const spotIdsRef = useRef<string[]>([]);
  const lastSpotsRef = useRef<CampingSpot[] | null>(null);
  const syncingRef = useRef(false);

  // Lytt på triggere som skjer mens appen lever (native poster selv når død).
  const triggerRef = useRef(onCheckInTriggered);
  useEffect(() => { triggerRef.current = onCheckInTriggered; }, [onCheckInTriggered]);

  useEffect(() => {
    if (!enabled || !pluginAvailable) return;
    let handle: ReturnType<typeof GeofenceCheckIn.addListener> | null = null;
    try {
      handle = GeofenceCheckIn.addListener('checkInTriggered', ({ spotId }) => {
        triggerRef.current(spotId);
      });
    } catch { /* plugin mangler i denne bygg-en */ }
    return () => { handle?.then(h => h.remove()).catch(() => {}); };
  }, [enabled]);

  // Re-registrer soner når brukeren beveger seg / plass-settet endres.
  useEffect(() => {
    if (!enabled || !pluginAvailable || !position || !currentUserUid) return;
    if (isOptedOut()) { GeofenceCheckIn.removeAllGeofences().catch(() => {}); return; }

    let cancelled = false;
    (async () => {
      // Billig port: hopp over dyr sortering hvis vi ikke har flyttet oss og
      // spots-referansen er uendret siden forrige synk.
      const spotsChanged = lastSpotsRef.current !== spots;
      if (anchorRef.current && !spotsChanged) {
        const moved = getDistance(
          anchorRef.current.latitude, anchorRef.current.longitude,
          position.latitude, position.longitude,
        );
        if (moved < 1000) return; // < 1 km og uendret sett → ingenting å gjøre
      }
      if (syncingRef.current) return;

      const eligibility = { currentUserUid, isDismissed: isDismissedRecently };
      const nearest = selectNearestEligibleSpots(spots, position, GEOFENCE_MAX_REGIONS, eligibility);
      const nextIds = nearest.map(s => s.id);

      if (!shouldResyncGeofences(anchorRef.current, position, spotIdsRef.current, nextIds)) {
        lastSpotsRef.current = spots;
        return;
      }

      const regions = buildGeofenceRegions(nearest, {
        radius: GEOFENCE_RADIUS_M,
        dwellMs: PROXIMITY_DWELL_MS,
        buildText,
      });

      syncingRef.current = true;
      try {
        const perm = await GeofenceCheckIn.checkPermissions();
        if (!perm.granted) return; // ingen «Tillat hele tiden» → ikke synk
        if (cancelled) return;
        await GeofenceCheckIn.removeAllGeofences();
        if (regions.length > 0) await GeofenceCheckIn.addGeofences({ geofences: regions });
        anchorRef.current = position;
        spotIdsRef.current = nextIds;
        lastSpotsRef.current = spots;
      } catch (e) {
        console.warn('Geofence sync failed:', e);
      } finally {
        syncingRef.current = false;
      }
    })();

    return () => { cancelled = true; };
  }, [enabled, spots, position, currentUserUid]);

  // Rydd opp når funksjonen slås av / bruker logger ut.
  useEffect(() => {
    if (pluginAvailable && (!enabled || !currentUserUid)) {
      GeofenceCheckIn.removeAllGeofences().catch(() => {});
      anchorRef.current = null;
      spotIdsRef.current = [];
    }
  }, [enabled, currentUserUid]);
}
